import { useCallback, useMemo } from "react";
import type { SessionOverviewResponse } from "@cabal/shared/domain/session/types.js";
import { LogLevel } from "@/hooks/telemetry";
import { isForbiddenApiError } from "@/utils/session-mutation-errors";
import { resolveUnreachableRouteDestination } from "@/lib/authReturnRoute";
import { toMembersMap } from "./initial-load-selectors";
import { parseDateOrNull } from "./session-date-selectors";
import {
  CHAT_PAGE_LIMIT,
  PollingSetupSource,
  SessionUpdateSource,
  toSessionCollab,
} from "./session-data-provider-utils";
import type {
  SessionPollingLogger,
  SessionPollingPollers,
  SessionSimplePoller,
  UseSessionPollingInput,
  SessionOverviewPoller,
} from "./session-polling.types";

type OverviewPollInput = Pick<
  UseSessionPollingInput,
  | "api"
  | "router"
  | "sessionId"
  | "sessionGenerationRef"
  | "haltStatusDebugLog"
  | "applySessionFromSource"
> & {
  source: PollingSetupSource;
  intervalMs: number;
};

type MembersPollInput = Pick<
  UseSessionPollingInput,
  | "api"
  | "sessionId"
  | "sessionGenerationRef"
  | "canFlashForPoll"
  | "flagMemberChanges"
  | "setMembers"
  | "updateActivitySinceTimestamp"
>;

type ProductsPollInput = Pick<
  UseSessionPollingInput,
  | "api"
  | "sessionId"
  | "sessionGenerationRef"
  | "timestamps"
  | "canFlashForPoll"
  | "mergeProductsAndFlagChanges"
  | "updateProductsSeen"
  | "updateProductsSinceTimestamp"
>;

type ChatPollInput = Pick<
  UseSessionPollingInput,
  | "api"
  | "sessionId"
  | "sessionGenerationRef"
  | "canFlashForPoll"
  | "mergePolledChatItems"
>;

type CollabsPollInput = Pick<
  UseSessionPollingInput,
  | "api"
  | "sessionId"
  | "sessionGenerationRef"
  | "timestamps"
  | "canFlashForPoll"
  | "mergePolledProductCollabItems"
  | "updateProductCollabsSinceTimestamp"
>;

type ProductReadsPollInput = Pick<
  UseSessionPollingInput,
  | "api"
  | "sessionId"
  | "sessionGenerationRef"
  | "timestamps"
  | "mergeProductReadItems"
  | "updateProductReadsSinceTimestamp"
>;

export function useSessionPollingPollers(
  input: UseSessionPollingInput,
): SessionPollingPollers {
  const pollSessionOverview = useSessionOverviewPoller(input);
  const pollMembers = useSessionMembersPoller(input);
  const pollProducts = useSessionProductsPoller(input);
  const pollChat = useSessionChatPoller(input);
  const pollCollabs = useSessionCollabsPoller(input);
  const pollProductReads = useSessionProductReadsPoller(input);

  return useMemo(
    () => ({
      pollSessionOverview,
      pollMembers,
      pollProducts,
      pollChat,
      pollCollabs,
      pollProductReads,
    }),
    [
      pollSessionOverview,
      pollMembers,
      pollProducts,
      pollChat,
      pollCollabs,
      pollProductReads,
    ],
  );
}

function useSessionOverviewPoller({
  api,
  router,
  sessionId,
  sessionGenerationRef,
  haltStatusDebugLog,
  applySessionFromSource,
}: UseSessionPollingInput): SessionOverviewPoller {
  return useCallback(
    async (input): Promise<void> => {
      const source = input.source;
      const intervalMs = input.intervalMs;
      await pollSessionOverview({
        api,
        router,
        sessionId,
        sessionGenerationRef,
        haltStatusDebugLog,
        applySessionFromSource,
        source,
        intervalMs,
      });
    },
    [
      api,
      router,
      sessionId,
      sessionGenerationRef,
      haltStatusDebugLog,
      applySessionFromSource,
    ],
  );
}

function useSessionMembersPoller({
  api,
  sessionId,
  sessionGenerationRef,
  canFlashForPoll,
  flagMemberChanges,
  setMembers,
  updateActivitySinceTimestamp,
}: UseSessionPollingInput): SessionSimplePoller {
  return useCallback(
    async (): Promise<void> =>
      pollMembers({
        api,
        sessionId,
        sessionGenerationRef,
        canFlashForPoll,
        flagMemberChanges,
        setMembers,
        updateActivitySinceTimestamp,
      }),
    [
      api,
      sessionId,
      sessionGenerationRef,
      canFlashForPoll,
      flagMemberChanges,
      setMembers,
      updateActivitySinceTimestamp,
    ],
  );
}

function useSessionProductsPoller({
  api,
  sessionId,
  sessionGenerationRef,
  timestamps,
  canFlashForPoll,
  mergeProductsAndFlagChanges,
  updateProductsSeen,
  updateProductsSinceTimestamp,
}: UseSessionPollingInput): SessionSimplePoller {
  return useCallback(
    async (): Promise<void> =>
      pollProducts({
        api,
        sessionId,
        sessionGenerationRef,
        timestamps,
        canFlashForPoll,
        mergeProductsAndFlagChanges,
        updateProductsSeen,
        updateProductsSinceTimestamp,
      }),
    [
      api,
      sessionId,
      sessionGenerationRef,
      timestamps,
      canFlashForPoll,
      mergeProductsAndFlagChanges,
      updateProductsSeen,
      updateProductsSinceTimestamp,
    ],
  );
}

function useSessionChatPoller({
  api,
  sessionId,
  sessionGenerationRef,
  canFlashForPoll,
  mergePolledChatItems,
}: UseSessionPollingInput): SessionSimplePoller {
  return useCallback(
    async (): Promise<void> =>
      pollChat({
        api,
        sessionId,
        sessionGenerationRef,
        canFlashForPoll,
        mergePolledChatItems,
      }),
    [
      api,
      sessionId,
      sessionGenerationRef,
      canFlashForPoll,
      mergePolledChatItems,
    ],
  );
}

function useSessionCollabsPoller({
  api,
  sessionId,
  sessionGenerationRef,
  timestamps,
  canFlashForPoll,
  mergePolledProductCollabItems,
  updateProductCollabsSinceTimestamp,
}: UseSessionPollingInput): SessionSimplePoller {
  return useCallback(
    async (): Promise<void> =>
      pollCollabs({
        api,
        sessionId,
        sessionGenerationRef,
        timestamps,
        canFlashForPoll,
        mergePolledProductCollabItems,
        updateProductCollabsSinceTimestamp,
      }),
    [
      api,
      sessionId,
      sessionGenerationRef,
      timestamps,
      canFlashForPoll,
      mergePolledProductCollabItems,
      updateProductCollabsSinceTimestamp,
    ],
  );
}

function useSessionProductReadsPoller({
  api,
  sessionId,
  sessionGenerationRef,
  timestamps,
  mergeProductReadItems,
  updateProductReadsSinceTimestamp,
}: UseSessionPollingInput): SessionSimplePoller {
  return useCallback(
    async (): Promise<void> =>
      pollProductReads({
        api,
        sessionId,
        sessionGenerationRef,
        timestamps,
        mergeProductReadItems,
        updateProductReadsSinceTimestamp,
      }),
    [
      api,
      sessionId,
      sessionGenerationRef,
      timestamps,
      mergeProductReadItems,
      updateProductReadsSinceTimestamp,
    ],
  );
}

async function pollSessionOverview(input: OverviewPollInput): Promise<void> {
  try {
    const generationAtPollStart = input.sessionGenerationRef.current;
    logOverviewPollStart({
      log: input.haltStatusDebugLog,
      source: input.source,
      capturedGeneration: generationAtPollStart,
      intervalMs: input.intervalMs,
    });
    const overview = await input.api.sessions.byId(input.sessionId).overview();
    const willDiscard =
      generationAtPollStart !== input.sessionGenerationRef.current;
    logOverviewPollReceived({
      log: input.haltStatusDebugLog,
      source: input.source,
      overview,
      capturedGeneration: generationAtPollStart,
      currentGeneration: input.sessionGenerationRef.current,
      willDiscard,
    });
    if (willDiscard) {
      return;
    }

    input.applySessionFromSource({
      nextSession: overview,
      source: SessionUpdateSource.Overview,
    });
  } catch (error) {
    handleOverviewPollError({
      error,
      source: input.source,
      router: input.router,
      log: input.haltStatusDebugLog,
    });
  }
}

async function pollMembers(input: MembersPollInput): Promise<void> {
  try {
    const generationAtPollStart = input.sessionGenerationRef.current;
    const canFlashForThisPoll = input.canFlashForPoll();
    const envelope = await input.api.sessions
      .byId(input.sessionId)
      .teamMembers();
    if (generationAtPollStart !== input.sessionGenerationRef.current) {
      return;
    }
    input.flagMemberChanges({
      items: envelope.items,
      canFlash: canFlashForThisPoll,
    });
    input.setMembers(toMembersMap(envelope.items));

    input.updateActivitySinceTimestamp(envelope.now);
  } catch {
    return;
  }
}

async function pollProducts(input: ProductsPollInput): Promise<void> {
  try {
    const generationAtPollStart = input.sessionGenerationRef.current;
    const canFlashForThisPoll = input.canFlashForPoll();
    const params = input.timestamps.current.productsSince
      ? { since: input.timestamps.current.productsSince }
      : {};

    const envelope = await input.api.sessions
      .byId(input.sessionId)
      .products.list({
        query: params,
      });
    if (generationAtPollStart !== input.sessionGenerationRef.current) {
      return;
    }

    input.mergeProductsAndFlagChanges({
      items: envelope.items,
      canFlash: canFlashForThisPoll,
    });

    envelope.items.forEach((product) => {
      input.updateProductsSeen({
        productId: product.id,
        patch: {
          lastChangedProduct: parseDateOrNull(product.updatedAt),
        },
      });
    });

    input.updateProductsSinceTimestamp(envelope.now);
  } catch {
    return;
  }
}

async function pollChat(input: ChatPollInput): Promise<void> {
  try {
    const generationAtPollStart = input.sessionGenerationRef.current;
    const canAnimateForThisPoll = input.canFlashForPoll();
    const response = await input.api.sessions.byId(input.sessionId).collabs({
      query: {
        sessionLevel: true,
        limit: CHAT_PAGE_LIMIT,
      },
    });
    if (generationAtPollStart !== input.sessionGenerationRef.current) {
      return;
    }
    const items = response.items.map((collab) => toSessionCollab({ collab }));
    input.mergePolledChatItems({
      items,
      canAnimate: canAnimateForThisPoll,
    });
  } catch {
    return;
  }
}

async function pollCollabs(input: CollabsPollInput): Promise<void> {
  try {
    const generationAtPollStart = input.sessionGenerationRef.current;
    const canFlashForThisPoll = input.canFlashForPoll();
    const loadedProductIds = Object.keys(
      input.timestamps.current.collabsSinceByProduct,
    );

    for (const productId of loadedProductIds) {
      const response = await input.api.sessions.byId(input.sessionId).collabs({
        query: {
          productId,
          limit: CHAT_PAGE_LIMIT,
        },
      });
      if (generationAtPollStart !== input.sessionGenerationRef.current) {
        return;
      }
      const items = response.items.map((collab) => toSessionCollab({ collab }));
      input.mergePolledProductCollabItems({
        productId,
        items,
        canFlash: canFlashForThisPoll,
      });
      input.updateProductCollabsSinceTimestamp({
        productId,
        collabsSince: new Date().toISOString(),
      });
    }
  } catch {
    return;
  }
}

async function pollProductReads(input: ProductReadsPollInput): Promise<void> {
  try {
    const generationAtPollStart = input.sessionGenerationRef.current;
    const query = input.timestamps.current.productReadsSince
      ? { changedSince: input.timestamps.current.productReadsSince }
      : {};
    const envelope = await input.api.sessions
      .byId(input.sessionId)
      .productReads({ query });
    if (generationAtPollStart !== input.sessionGenerationRef.current) {
      return;
    }
    input.mergeProductReadItems({
      items: envelope.items,
      replace: false,
    });
    input.updateProductReadsSinceTimestamp(envelope.now);
  } catch {
    return;
  }
}

function logOverviewPollStart(input: {
  log: SessionPollingLogger;
  source: PollingSetupSource;
  capturedGeneration: number;
  intervalMs: number;
}): void {
  input.log(
    `poll-start (${input.source})`,
    {
      capturedGeneration: input.capturedGeneration,
      intervalMs: input.intervalMs,
    },
    LogLevel.Info,
  );
}

function logOverviewPollReceived(input: {
  log: SessionPollingLogger;
  source: PollingSetupSource;
  overview: SessionOverviewResponse;
  capturedGeneration: number;
  currentGeneration: number;
  willDiscard: boolean;
}): void {
  input.log(
    `poll-received (${input.source})`,
    {
      returnedStatus: input.overview.status,
      returnedHaltReason: input.overview.haltInfo?.reason ?? null,
      capturedGeneration: input.capturedGeneration,
      currentGeneration: input.currentGeneration,
      willDiscard: input.willDiscard,
    },
    input.willDiscard ? LogLevel.Warning : LogLevel.Info,
  );
}

function handleOverviewPollError(input: {
  error: unknown;
  source: PollingSetupSource;
  router: UseSessionPollingInput["router"];
  log: SessionPollingLogger;
}): void {
  const isForbidden = isForbiddenApiError(input.error);
  input.log(
    `poll-error (${input.source})`,
    { message: String(input.error), isForbidden },
    LogLevel.Warning,
  );

  if (isForbidden) {
    input.router.replace(resolveUnreachableRouteDestination("/forbidden"));
  }
}
